// A plan Copilot put up for approval, in four states on one card:
//   before   what changes, what Copilot checked, what it means (consequences),
//            then Approve. Held back while Copilot is still explaining, and
//            never approvable when it would fail as written.
//   running  each step ticks off as it finishes (the card polls the plan).
//   done     one line with Undo (only where a coded reverse exists) and Details.
//   failed   which step, why, what stayed unchanged, and a way to fix it.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notification, Popconfirm } from "antd";
import { toDisplayBrand } from "../../utils/displayBrand";
import {
  approveOperatorPlanApi, rejectOperatorPlanApi, getOperatorPlanApi, undoOperatorPlanApi, cleanupOperatorPlanApi,
} from "../../store/actions/operatorAction";
import { formatWhen } from "./time";
import {
  Card, CardTop, Kicker, CardTitle, CardText, Section, SectionLabel, StepList, StepItem, Tags, Tag,
  CardFoot, Primary, Secondary, TextButton, Receipt, SmallButton, Spinner, SoftNote,
} from "./CardStyle";
import { DoneMark, DoneSolid, FailMark, WaitMark, PersonMark, UndoSolid, RunningMark } from "./CardIcons";
import { TestCallResult } from "./MoreCards";
import { QUESTIONNAIRE_ACTIONS, QuestionnaireOutline, QuestionnaireResult, QuestionnaireView, QuestionnaireCheckNote, useOnReturn } from "./QuestionnairePreview";

const outcomeText = (r) => {
  if (!r) return "";
  const res = r.result || {};
  if (r.outcome === "simulated") return res.would || "";
  if (r.outcome === "done") return res.summary || res.would || "";
  if (r.outcome === "blocked") return res.reason || "";
  if (r.outcome === "failed") return res.error || res.reason || "";
  if (r.outcome === "skipped_manual") return (res.manual_steps || []).join(" ") || "A person needs to do this step.";
  return "";
};

const stepName = (a) => toDisplayBrand(a.text || a.label || a.action);

/** The steps with their marks: numbers before a run, outcomes during and after. */
function Steps({ plan, results, running, compact, hideResult }) {
  const firstOpen = running ? plan.actions.findIndex((a) => !results[a.id]) : -1;
  return (
    <StepList>
      {plan.actions.map((a, i) => {
        const r = results[a.id];
        const manual = a.manual || a.runs_live === false;
        let mark = <span className="num">{i + 1}</span>;
        if (r) {
          mark = r.outcome === "done" || r.outcome === "simulated" ? <DoneMark />
            : r.outcome === "skipped_manual" ? <PersonMark /> : <FailMark />;
        } else if (running) {
          mark = i === firstOpen ? <RunningMark /> : <WaitMark />;
        } else if (manual && plan.status === "proposed") {
          mark = <PersonMark />;
        }
        const after = r ? outcomeText(r) : null;
        const failed = r && (r.outcome === "failed" || r.outcome === "blocked");
        return (
          <StepItem key={a.id}>
            <span className={r || running || (manual && plan.status === "proposed") ? "ico" : ""}>{mark}</span>
            <span>
              <b>{stepName(a)}</b>
              {after ? <span className={failed ? "x" : "r"}>{toDisplayBrand(after)}</span>
                : running && i === firstOpen ? <span className="w">Working on it…</span>
                : running ? <span className="w">Waiting for the step before</span>
                : manual && plan.status === "proposed" ? <span className="w">A person does this step{(a.manual_steps || []).length ? `: ${toDisplayBrand(a.manual_steps.join(" "))}` : ""}</span>
                : !compact && a.rationale ? <span className="w">{toDisplayBrand(a.rationale)}</span> : null}
              {QUESTIONNAIRE_ACTIONS.has(a.action) && !r && !running ? <QuestionnaireOutline params={a.params} compact={compact} /> : null}
              {QUESTIONNAIRE_ACTIONS.has(a.action) && r && r.outcome === "done" && !hideResult ? <QuestionnaireResult result={r.result} compact={compact} /> : null}
            </span>
          </StepItem>
        );
      })}
    </StepList>
  );
}


/**
 * Undo runs for real, and undoing has its own effects (undoing a pause
 * resumes fielding and spending): say what it will do and ask first.
 */
function UndoConfirm({ plan, onConfirm, children }) {
  const u = plan.undo_preview || {};
  const warn = (u.consequences || []).filter((t) => t.tone !== "grey");
  return (
    <Popconfirm
      title="Undo this?"
      okText="Undo it"
      cancelText="Keep it"
      onConfirm={onConfirm}
      description={(
        <div style={{ maxWidth: 300, fontSize: 12.5, lineHeight: 1.5 }}>
          {(u.steps || []).length ? <div>This will: {u.steps.map((t) => toDisplayBrand(String(t).replace(/\.+$/, ""))).join("; ")}.</div> : <div>This reverses what ran.</div>}
          {warn.length ? <div style={{ marginTop: 6, color: "#9A4A06" }}>{warn.map((t) => t.label).join(" · ")}</div> : null}
        </div>
      )}
    >
      {children}
    </Popconfirm>
  );
}

/** Confirm before removing what a stopped plan created: what goes, and what stays. */
function CleanupConfirm({ plan, onConfirm, children }) {
  const c = plan.cleanup_preview || {};
  return (
    <Popconfirm
      title="Remove what this plan created?"
      okText="Remove it"
      cancelText="Keep it"
      onConfirm={onConfirm}
      description={(
        <div style={{ maxWidth: 320, fontSize: 12.5, lineHeight: 1.5 }}>
          {(c.steps || []).length ? <div>This will: {c.steps.map((t) => toDisplayBrand(String(t).replace(/\.+$/, ""))).join("; ")}.</div> : null}
          {(c.kept || []).length ? <div style={{ marginTop: 6, color: "#6D6A67" }}>Stays: {c.kept.map((t) => toDisplayBrand(String(t))).join("; ")}.</div> : null}
          <div style={{ marginTop: 6, color: "#9A4A06" }}>Deleted items cannot be restored.</div>
        </div>
      )}
    >
      {children}
    </Popconfirm>
  );
}

const PlanCard = ({ plan: given, onUpdated, held = false, compact = false, onRevise, hideActions = false }) => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(given);
  const [busy, setBusy] = useState(null);
  const [details, setDetails] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => { setPlan(given); }, [given]);
  useEffect(() => () => clearInterval(pollRef.current), []);
  // Back from the Survey Builder: a card still waiting re-reads itself, so a
  // card made before the person's own edits stands down instead of writing over them.
  useOnReturn(() => {
    if (plan.status !== "proposed") return;
    getOperatorPlanApi(plan._id).then((out) => { if (out?.plan) setPlan(out.plan); }).catch(() => {});
  });

  const live = !!plan.executor_live;
  const results = plan.results || {};
  const running = busy === "approve" || plan.status === "approved";
  const blocked = plan.status === "proposed" && (plan.blocking || []).length > 0;
  const summary = toDisplayBrand(plan.summary || "Plan");
  // An interviewer test call's result is the substance: always shown in full.
  const testCalls = plan.actions
    .filter((a) => a.action === "agent.test" && results[a.id] && results[a.id].result && results[a.id].result.test_call)
    .map((a) => <TestCallResult key={`t-${a.id}`} result={results[a.id].result} onRevise={onRevise} when={results[a.id].at ? formatWhen(results[a.id].at) : null} />);
  // A one-step plan's receipt reads as the step itself (shorter, with names).
  const receiptTitle = plan.actions.length === 1 && plan.actions[0].text ? toDisplayBrand(plan.actions[0].text).replace(/\.$/, "") : summary;
  // Once run, a one-step plan's receipt says what happened, in the step's own
  // words ("Deleted 69 conversations ..."), not the instruction it was given.
  const ranSummary = plan.actions.length === 1 && results[plan.actions[0].id] && results[plan.actions[0].id].outcome === "done"
    ? String(results[plan.actions[0].id].result?.summary || "").split(/(?<=\.)\s/)[0].replace(/\.$/, "") : "";
  const doneTitle = ranSummary ? toDisplayBrand(ranSummary) : receiptTitle;
  const doneCount = Object.values(results).filter((r) => r.outcome === "done").length;
  const manual = plan.actions.filter((a) => a.manual || a.runs_live === false).length;

  const poll = (on) => {
    clearInterval(pollRef.current);
    if (!on) return;
    pollRef.current = setInterval(async () => {
      try { const out = await getOperatorPlanApi(plan._id); if (out?.plan) setPlan(out.plan); } catch (_) { /* next tick */ }
    }, 1500);
  };

  const act = async (kind) => {
    setBusy(kind);
    if (kind === "approve") poll(true);
    try {
      const out = kind === "approve" ? await approveOperatorPlanApi(plan._id)
        : kind === "undo" ? await undoOperatorPlanApi(plan._id)
        : kind === "cleanup" ? await cleanupOperatorPlanApi(plan._id)
        : await rejectOperatorPlanApi(plan._id);
      if (out?.plan) setPlan(out.plan);
      onUpdated?.(kind === "undo" || kind === "cleanup" ? { ...out, live: true } : out);
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || "That did not work. Try again." });
    } finally {
      poll(false);
      setBusy(null);
    }
  };

  // ── Stood down: the questionnaire was edited after this card was made ──
  if (plan.status === "proposed" && plan.stale_edit) {
    const t = plan.stale_edit.title ? `"${toDisplayBrand(plan.stale_edit.title)}"` : "the questionnaire";
    return (
      <Receipt role="status">
        <span className="t">
          <b>Replaced by your edits in the Survey Builder</b>
          <span>{t} was edited after this card was made{plan.stale_edit.edited_at ? ` (${formatWhen(plan.stale_edit.edited_at)})` : ""}. Your version stands; nothing on this card was applied.</span>
        </span>
      </Receipt>
    );
  }

  // ── Finished: one line ────────────────────────────────────────────────
  if (plan.status === "superseded") {
    return <Receipt><span className="t"><span>Replaced by a newer version of this plan.</span></span></Receipt>;
  }
  if (plan.status === "rejected") {
    return (
      <Receipt>
        <span className="t"><b>Not run: {receiptTitle}</b><span>{plan.decided_at ? formatWhen(plan.decided_at) : ""} · Nothing changed</span></span>
      </Receipt>
    );
  }
  const detailBlock = details ? (
    <Card><Section style={{ borderTop: 0 }}><Steps plan={plan} results={results} compact={compact} hideResult /></Section></Card>
  ) : null;
  // A questionnaire that was drafted: its questions under the receipt.
  const drafted = plan.actions
    .filter((a) => QUESTIONNAIRE_ACTIONS.has(a.action) && results[a.id] && results[a.id].outcome === "done")
    .map((a) => <QuestionnaireResult key={`q-${a.id}`} result={results[a.id].result} compact={compact} />);
  if (plan.status === "applied" && plan.undone_by) {
    return (
      <>
        <Receipt role="status">
          <UndoSolid />
          <span className="t"><b>Undone: {receiptTitle}</b><span>{plan.undone_at ? formatWhen(plan.undone_at) : ""} · Put back as it was</span></span>
          <span className="acts"><TextButton type="button" onClick={() => setDetails((v) => !v)}>{details ? "Hide details" : "Details"}</TextButton></span>
        </Receipt>
        {detailBlock}
      </>
    );
  }
  if (plan.status === "applied" || plan.status === "simulated") {
    const sim = plan.status === "simulated";
    return (
      <>
        <Receipt role="status" $tone={sim ? undefined : "green"}>
          {sim ? <UndoSolid /> : <DoneSolid />}
          <span className="t">
            <b>{sim ? `Simulated: ${receiptTitle}` : doneTitle}</b>
            <span>{plan.decided_at ? formatWhen(plan.decided_at) : ""}{sim ? " · Nothing changed on the platform" : ` · ${doneCount} ${doneCount === 1 ? "step" : "steps"} done${plan.undoable ? " · Can be undone" : ""}`}</span>
          </span>
          <span className="acts">
            {plan.undoable && !hideActions ? (
              <UndoConfirm plan={plan} onConfirm={() => act("undo")}>
                <SmallButton type="button" disabled={!!busy}>{busy === "undo" ? "Undoing…" : "Undo"}</SmallButton>
              </UndoConfirm>
            ) : null}
            <TextButton type="button" onClick={() => setDetails((v) => !v)}>{details ? "Hide details" : "Details"}</TextButton>
          </span>
        </Receipt>
        {detailBlock}
        {plan.status === "applied" ? drafted : null}
        {plan.status === "applied" ? <QuestionnaireCheckNote checks={plan.questionnaire_check} onRevise={hideActions ? null : onRevise} /> : null}
        {testCalls}
      </>
    );
  }

  // ── Running or failed: the steps with their outcomes ─────────────────
  if (running || plan.status === "failed") {
    const failedAt = plan.actions.findIndex((a) => results[a.id] && ["failed", "blocked"].includes(results[a.id].outcome));
    const at = running
      ? Math.min(plan.actions.findIndex((a) => !results[a.id]) + 1 || plan.actions.length, plan.actions.length)
      : failedAt >= 0 ? failedAt + 1 : Math.max(Object.keys(results).length, 1);
    const failure = failedAt >= 0 ? outcomeText(results[plan.actions[failedAt].id]) : "";
    return (
      <>
      <Card aria-live="polite">
        <CardTop>
          <div className="row">
            <Kicker $tone={running ? "blue" : "red"}>{running ? `Running, step ${at} of ${plan.actions.length}` : `Stopped at step ${at}`}</Kicker>
            <span className="meta">{plan.decided_at ? `Approved ${formatWhen(plan.decided_at)}` : ""}</span>
          </div>
          <CardTitle $big>{summary}</CardTitle>
          {!running ? <CardText>Steps after it were not run. {plan.cleaned_by ? "What it created was removed." : doneCount ? "What already ran stays as it is." : "Nothing was changed."}</CardText> : null}
        </CardTop>
        <Section><Steps plan={plan} results={results} running={running} compact={compact} /></Section>
        <CardFoot>
          {running ? <span className="note">You can keep chatting. This card updates as each step finishes.</span> : (
            <>
              {onRevise ? <Primary type="button" onClick={() => onRevise(`Step ${at} of "${plan.summary}" did not run: ${failure} Fix it and try again.`)}>Fix it with Copilot</Primary> : null}
              {plan.undoable ? (
                <UndoConfirm plan={plan} onConfirm={() => act("undo")}>
                  <Secondary type="button" disabled={!!busy}>{busy === "undo" ? "Undoing…" : "Undo what ran"}</Secondary>
                </UndoConfirm>
              ) : null}
              {plan.cleanable && !hideActions ? (
                <CleanupConfirm plan={plan} onConfirm={() => act("cleanup")}>
                  <Secondary type="button" disabled={!!busy}>{busy === "cleanup" ? "Removing…" : "Remove what this plan created"}</Secondary>
                </CleanupConfirm>
              ) : null}
            </>
          )}
        </CardFoot>
      </Card>
      {!running ? <QuestionnaireCheckNote checks={plan.questionnaire_check} onRevise={hideActions ? null : onRevise} /> : null}
      {testCalls}
      </>
    );
  }

  // ── Nothing Copilot may run here: a person does it ────────────────────
  const allManual = plan.status === "proposed" && !blocked && plan.actions.length > 0
    && plan.actions.every((a) => a.manual || a.runs_live === false);
  if (allManual) {
    const switchedOff = (plan.issues || []).some((i) => /not enabled yet/.test(i));
    return (
      <Card>
        <CardTop>
          <div className="row"><Kicker $tone="grey">A person does this one</Kicker></div>
          <CardTitle $big>{summary}</CardTitle>
          <CardText>{switchedOff
            ? "Copilot is not switched on for this kind of change here. A person does it, or a super-admin can switch it on in Manage, Settings."
            : "Copilot cannot make this change from here. These are the steps for a person."}</CardText>
        </CardTop>
        <Section><Steps plan={plan} results={results} compact={compact} /></Section>
        {hideActions ? null : (
          <CardFoot>
            {switchedOff ? <Secondary type="button" onClick={() => navigate("/manage")}>Copilot settings</Secondary> : null}
            <TextButton type="button" $muted $push disabled={!!busy} onClick={() => act("reject")}>Got it</TextButton>
          </CardFoot>
        )}
      </Card>
    );
  }

  // ── Before approval ──────────────────────────────────────────────────
  // What Copilot checked: plain strings, or {key, value} facts from the plan.
  const fact = (e) => (typeof e === "string" ? e
    : e && e.value != null && typeof e.value !== "object" ? `${e.key ? `${String(e.key).replace(/_/g, " ")}: ` : ""}${e.value}` : null);
  const evidence = [...new Set([
    typeof plan.based_on === "string" ? plan.based_on : null,
    ...plan.actions.flatMap((a) => (a.evidence || []).map(fact)),
  ].filter(Boolean))].slice(0, 4);
  // The questionnaire a step works on (study, interviewer, media, push...):
  // shown in full before approval, so the person sees exactly what will run.
  const questionnaireIds = [...new Set(plan.actions
    .map((a) => a.params && a.params.survey_id)
    .filter((v) => typeof v === "string" && /^[a-f0-9]{24}$/i.test(v)))];
  return (
    <Card>
      <CardTop>
        <div className="row">
          {blocked ? <Kicker $tone="grey">Being corrected</Kicker>
            : live ? <Kicker $tone="amber">Needs your OK</Kicker>
            : <Kicker $tone="grey">Preview: runs as a simulation</Kicker>}
          {plan.version > 1 ? <span className="meta">Version {plan.version}</span> : null}
        </div>
        <CardTitle $big>{summary}</CardTitle>
        {blocked ? <CardText>This version would fail as written ({toDisplayBrand(plan.blocking[0])}), so it cannot be approved. {held ? "Copilot is replacing it." : "Ask Copilot to correct it."}</CardText> : null}
      </CardTop>
      {!blocked && questionnaireIds.length ? (
        <Section>
          {questionnaireIds.map((sid) => <QuestionnaireView key={sid} surveyId={sid} compact={compact} label="The questionnaire you are approving" />)}
        </Section>
      ) : null}
      {!blocked ? (
        <Section>
          <SectionLabel>What changes</SectionLabel>
          <Steps plan={plan} results={results} compact={compact} />
        </Section>
      ) : null}
      {!blocked && evidence.length ? (
        <Section>
          <SectionLabel>What Copilot checked</SectionLabel>
          {evidence.map((e, i) => <CardText key={i} style={{ marginTop: i ? 4 : 0 }}>{toDisplayBrand(e)}</CardText>)}
        </Section>
      ) : null}
      {!blocked && (plan.consequences || []).length ? (
        <Section>
          <Tags>{plan.consequences.map((t) => <Tag key={t.key} $tone={t.tone}>{t.label}</Tag>)}</Tags>
        </Section>
      ) : null}
      {hideActions ? null : (
        <CardFoot>
          {held ? (
            <SoftNote><Spinner $size={14} />{blocked ? "Preparing a corrected version" : "Copilot is finishing its explanation"}</SoftNote>
          ) : blocked ? (
            <>
              {onRevise ? <Secondary type="button" onClick={() => onRevise(`Correct the plan "${plan.summary}": ${plan.blocking[0]}`)}>Ask Copilot to fix it</Secondary> : null}
              <TextButton type="button" $muted $push disabled={!!busy} onClick={() => act("reject")}>Not now</TextButton>
            </>
          ) : (
            <>
              {live && (plan.consequences || []).some((t) => t.key === "no_undo") ? (
                // A step that cannot be undone asks once more, naming it.
                <Popconfirm
                  title="This can't be undone"
                  okText="Yes, run it"
                  cancelText="Not yet"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => act("approve")}
                  description={<div style={{ maxWidth: 300, fontSize: 12.5, lineHeight: 1.5 }}>{(plan.actions || []).filter((a) => (a.consequences || []).some((t) => t.key === "no_undo")).map((a) => stepName(a)).join("; ") || toDisplayBrand(plan.summary)}</div>}
                >
                  <Primary type="button" disabled={!!busy}>{busy === "approve" ? "Starting…" : "Approve and run"}</Primary>
                </Popconfirm>
              ) : (
                <Primary type="button" disabled={!!busy} onClick={() => act("approve")}>{busy === "approve" ? "Starting…" : live ? "Approve and run" : "Approve and simulate"}</Primary>
              )}
              {onRevise ? <Secondary type="button" disabled={!!busy} onClick={() => onRevise("Change the plan: ")}>Change it</Secondary> : null}
              <TextButton type="button" $muted $push disabled={!!busy} onClick={() => act("reject")}>Not now</TextButton>
              {live && manual ? <span className="note" style={{ flexBasis: "100%" }}>{manual} {manual === 1 ? "step is" : "steps are"} for a person; Copilot runs the rest.</span> : null}
            </>
          )}
        </CardFoot>
      )}
    </Card>
  );
};

export default PlanCard;
